import * as React from 'react';

import type { ImportEventTrigger } from '@gitbook/app-analytics';
import * as analytics from '@gitbook/app-analytics/client';
import {
    Alert,
    Button,
    Card,
    CardActions,
    CardBody,
    CardImage,
    CardImageLayout,
    CardWrapperButton,
    type CardWrapperButtonProps,
    Description,
    Dialog,
    DialogBody,
    DialogHeader,
    DialogRoot,
    DialogTrigger,
    Heading,
    ImportSourceIllustration,
    ProgressiveImage,
    Separator,
    useDialogClose,
} from '@gitbook/spine-react';

import { siteMultipleClients } from '../../assets';
import { useSetAnalyticsScreen } from '../../navigation';
import { useCurrentOrganizationId } from '../../state';
import { ImportFilesDialog } from './ImportFilesDialog';
import { ImportWebsiteDialog } from './ImportWebsiteDialog';
import { type ImportResolvedTarget, type ImportTarget, ImportTargetContext } from './TargetContext';

export interface ImportContentDialogProps {
    /**
     * What triggered the import (for analytics purposes).
     */
    trigger: ImportEventTrigger;
    /**
     * The target where to import the content.
     */
    target: ImportTarget;
    /**
     * Callback when the import starts.
     */
    onStart?: (target: ImportResolvedTarget) => void | Promise<void>;
    /**
     * Default source.
     */
    defaultSource?: 'file' | 'website';
    /**
     * Optional title to adapt the dialog to the entry point.
     */
    title?: string;
}

/**
 * Modal to import content into a target.
 */
export function ImportContentDialog(props: {
    /**
     * What triggered the import (for analytics purposes).
     */
    trigger: ImportEventTrigger;
    /**
     * The target where to import the content.
     */
    target: ImportTarget;
    /**
     * Callback when the import starts.
     */
    onStart?: (target: ImportResolvedTarget) => void | Promise<void>;
    /**
     * Default source.
     */
    defaultSource?: 'file' | 'website';
    /**
     * Optional title to adapt the dialog to the entry point.
     */
    title?: string;
}) {
    return (
        <Dialog size={{ width: 664 }}>
            <ImportContentDialogContent {...props} />
        </Dialog>
    );
}

function ImportContentDialogContent(props: ImportContentDialogProps) {
    const { trigger, target, defaultSource, onStart, title = 'Import' } = props;
    const closeDialog = useDialogClose();
    const handleStart = async (target: ImportResolvedTarget) => {
        await onStart?.(target);
        closeDialog();
    };
    return (
        <>
            <DialogHeader>
                <Heading>{title}</Heading>
                <Description>Import from different sources straight into GitBook.</Description>
            </DialogHeader>
            <ImportTargetContext target={target}>
                {({ resolveTarget }) => {
                    return (
                        <ImportContentDialogBody
                            trigger={trigger}
                            defaultSource={defaultSource}
                            onStart={handleStart}
                            resolveTarget={resolveTarget}
                        />
                    );
                }}
            </ImportTargetContext>
        </>
    );
}

function ImportContentDialogBody(props: {
    trigger: ImportEventTrigger;
    defaultSource?: 'file' | 'website';
    onStart: (target: ImportResolvedTarget) => void | Promise<void>;
    resolveTarget: (() => Promise<ImportResolvedTarget>) | null;
}) {
    const { trigger, defaultSource, onStart, resolveTarget } = props;

    if (!resolveTarget) {
        return (
            <DialogBody>
                <Alert kind="warning" title="You do not have permission to import here." />
            </DialogBody>
        );
    }

    return (
        <DialogBody className="flex flex-col gap-6">
            <ImportAnalyticsScreen trigger={trigger} />
            <div className="flex gap-6">
                <DialogRoot defaultOpen={defaultSource === 'website'}>
                    <DialogTrigger>
                        <ImportCardButton
                            source="website"
                            title="Online docs"
                            description="Import from any public documentation."
                        />
                    </DialogTrigger>
                    <ImportWebsiteDialog
                        resolveTarget={resolveTarget}
                        onStart={onStart}
                        trigger={trigger}
                    />
                </DialogRoot>
                <DialogRoot defaultOpen={defaultSource === 'file'}>
                    <DialogTrigger>
                        <ImportCardButton
                            source="files"
                            title="File"
                            description="Import from Markdown, HTML or ZIP files."
                        />
                    </DialogTrigger>
                    <ImportFilesDialog
                        resolveTarget={resolveTarget}
                        onStart={onStart}
                        trigger={trigger}
                    />
                </DialogRoot>
            </div>

            <Separator />

            <NeedHelpCard />
        </DialogBody>
    );
}

/**
 * Track the import screen in analytics.
 */
function ImportAnalyticsScreen(props: { trigger: ImportEventTrigger }) {
    const { trigger } = props;
    useSetAnalyticsScreen({ screen: 'import_start', trigger }, [trigger]);
    return null;
}

const ImportCardButton = function ImportCardButton({
    ref,
    ...props
}: ({
    source: 'files' | 'website';
    title: string;
    description: string;
} & Omit<CardWrapperButtonProps, 'children' | 'image' | 'illustration'>) & {
    ref?: React.ForwardedRef<HTMLDivElement>;
}) {
    const { source, title, description, className, ...rest } = props;
    return (
        <CardWrapperButton
            ref={ref}
            className="flex-1"
            {...rest}
            illustration={
                <ImportSourceIllustration source={source} className="aspect-[300/128] px-9" />
            }
        >
            <div>
                <Heading level={4}>{title}</Heading>
                <Description>{description}</Description>
            </div>
        </CardWrapperButton>
    );
};

function NeedHelpCard() {
    const organizationId = useCurrentOrganizationId();
    return (
        <Card>
            <CardImageLayout>
                <CardBody>
                    <Heading>Need help migrating your docs? </Heading>
                    <Description>
                        Our white-glove migration service ensures your content moves smoothly into
                        GitBook.
                    </Description>
                    <CardActions>
                        <Button
                            kind="secondary"
                            label="Talk to our team"
                            target="_blank"
                            href="https://www.gitbook.com/enterprise"
                            onPress={() => {
                                if (organizationId) {
                                    analytics.logEvent(
                                        analytics.createOrgEvent(organizationId, {
                                            type: 'organization_open_enterprise_cta',
                                        })
                                    );
                                }
                            }}
                        />
                    </CardActions>
                </CardBody>
                <CardImage>
                    <ProgressiveImage src={siteMultipleClients} width={242} height={96} />
                </CardImage>
            </CardImageLayout>
        </Card>
    );
}
